import { useState } from "react";
import { Utensils, X, ChevronUp, ChevronDown, Plus, Trash2 } from "lucide-react";
import AddFoodPopover from "@/components/lab/AddFoodPopover";
import { makeMeal, foodLine, recomputeLine, mealTotals, dietTotals } from "@/lib/diet";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { fmt } from "@/lib/format";
import { toast } from "sonner";

const NutriTable = ({ f }) => {
  const b = f.base || { kcal: 0, carbo: 0, grassi: 0, proteine: 0 };
  const rows = [
    ["Kcal", fmt(b.kcal), fmt(f.kcal), "hsl(var(--c-kcal))"],
    ["Carboidrati", `${fmt(b.carbo, 1)} g`, `${fmt(f.carbo, 1)} g`, "hsl(var(--c-carb))"],
    ["Proteine", `${fmt(b.proteine, 1)} g`, `${fmt(f.protAnimali + f.protVegetali, 1)} g`, "hsl(var(--c-prot))"],
    ["Grassi", `${fmt(b.grassi, 1)} g`, `${fmt(f.grassi, 1)} g`, "hsl(var(--c-fat))"],
  ];
  return (
    <div className="w-56" data-testid={`nutri-table-${f.id}`}>
      <div className="mb-1.5 font-display text-sm font-bold">{f.nome}</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="py-1 text-left font-semibold"></th>
            <th className="py-1 text-right font-semibold">{f.unit === "pezzo" ? "1 pz" : "100 g"}</th>
            <th className="py-1 text-right font-semibold">{fmt(f.grammi)} {f.unit === "pezzo" ? "pz" : "g"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, per100, portion, color]) => (
            <tr key={label} className="border-t border-white/10">
              <td className="py-1 text-left" style={{ color }}>{label}</td>
              <td className="py-1 text-right font-mono-num text-muted-foreground">{per100}</td>
              <td className="py-1 text-right font-mono-num" style={{ color }}>{portion}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TargetBar = ({ label, value, target, color }) => {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono-num" style={{ color }}>{fmt(value, 1)}g{target > 0 ? ` / ${fmt(target)}g` : ""}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-white/5">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export default function DietEditor({ diet, onChange, foods, onCreateFood, macroGrams = {}, totalsLabel }) {
  const [newMealName, setNewMealName] = useState("");
  const update = (fn) => onChange(fn(diet));

  const addMeal = () => {
    if (!newMealName.trim()) { toast.error("Inserisci un nome per il pasto"); return; }
    update((d) => ({ ...d, meals: [...d.meals, makeMeal(newMealName.toUpperCase())] }));
    setNewMealName("");
  };
  const removeMeal = (mealId) => update((d) => ({ ...d, meals: d.meals.filter((m) => m.id !== mealId) }));
  const moveMeal = (mealId, dir) => update((d) => {
    const meals = [...d.meals];
    const i = meals.findIndex((m) => m.id === mealId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= meals.length) return d;
    [meals[i], meals[j]] = [meals[j], meals[i]];
    return { ...d, meals };
  });
  const addFood = (mealId, food, g) => update((d) => ({
    ...d, meals: d.meals.map((m) => (m.id === mealId ? { ...m, foods: [...m.foods, foodLine(food, g)] } : m)),
  }));
  const updateGrams = (mealId, foodId, g) => update((d) => ({
    ...d, meals: d.meals.map((m) => (m.id === mealId ? { ...m, foods: m.foods.map((f) => (f.id === foodId ? recomputeLine(f, g) : f)) } : m)),
  }));
  const removeFood = (mealId, foodId) => update((d) => ({
    ...d, meals: d.meals.map((m) => (m.id === mealId ? { ...m, foods: m.foods.filter((f) => f.id !== foodId) } : m)),
  }));

  const dt = dietTotals(diet);

  return (
    <div className="space-y-4">
      <section className="panel p-5" data-testid="diet-totals">
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="font-display text-sm font-bold tracking-wide text-muted-foreground">{totalsLabel || `TOTALE GIORNALIERO — ${diet.nome}`}</h3>
          <span className="metric-num text-2xl text-[hsl(var(--c-kcal))]">{fmt(dt.kcal)} <span className="text-sm text-muted-foreground">kcal</span></span>
        </div>
        <div className="space-y-3">
          <TargetBar label="Carboidrati" value={dt.carbo} target={macroGrams.carbG || 0} color="hsl(var(--c-carb))" />
          <TargetBar label="Proteine Animali" value={dt.protAnimali} target={macroGrams.protG || 0} color="hsl(var(--c-prot))" />
          <TargetBar label="Grassi" value={dt.grassi} target={macroGrams.fatG || 0} color="hsl(var(--c-fat))" />
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Proteine vegetali escluse dal totale: <span className="font-mono-num">{fmt(dt.protVegetali, 1)} g</span> (conteggiate solo kcal, carboidrati e grassi)
        </p>
      </section>

      <section className="panel p-4" data-testid="diet-note">
        <div className="field-label mb-1.5">Note — {diet.nome}</div>
        <textarea
          rows={3}
          value={diet.note || ""}
          onChange={(e) => onChange({ ...diet, note: e.target.value })}
          placeholder="Note sulla dieta: indicazioni, sostituzioni, integrazione, idratazione, orari dei pasti..."
          className="lab-input resize-none"
          data-testid="diet-note-input"
        />
      </section>

      <div className="space-y-4">
        {diet.meals.map((meal, mi) => {
          const t = mealTotals(meal);
          return (
            <div key={meal.id} className="panel overflow-hidden" data-testid={`meal-${mi}`}>
              <div className="flex items-center justify-between gap-2 border-b border-white/5 px-4 py-3">
                <div className="flex items-center gap-2">
                  <Utensils className="h-4 w-4 text-primary" />
                  <span className="font-display text-sm font-bold tracking-wide">{meal.nome}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => moveMeal(meal.id, -1)} disabled={mi === 0} className="rounded-md border border-white/10 p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30" data-testid={`meal-up-${mi}`} title="Sposta su"><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => moveMeal(meal.id, 1)} disabled={mi === diet.meals.length - 1} className="rounded-md border border-white/10 p-1.5 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30" data-testid={`meal-down-${mi}`} title="Sposta giù"><ChevronDown className="h-4 w-4" /></button>
                  <AddFoodPopover foods={foods} onCreate={onCreateFood} onAdd={(f, g) => addFood(meal.id, f, g)} />
                  <button onClick={() => removeMeal(meal.id)} className="rounded-md border border-white/10 p-1.5 text-muted-foreground transition-colors hover:text-destructive" data-testid={`meal-delete-${mi}`}><X className="h-4 w-4" /></button>
                </div>
              </div>

              <div className="px-4 py-3">
                {meal.foods.length === 0 ? (
                  <div className="py-3 text-sm italic text-muted-foreground/70">Nessun alimento inserito</div>
                ) : (
                  <div className="space-y-1.5">
                    {meal.foods.map((f) => (
                      <div key={f.id} className="flex items-center gap-3 rounded-lg bg-black/20 px-3 py-2" data-testid={`food-row-${f.id}`}>
                        <TooltipProvider delayDuration={2000}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="min-w-0 flex-1 cursor-help truncate text-sm" data-testid={`food-name-${f.id}`}>{f.nome}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top" align="start" className="border-white/10 bg-[hsl(var(--popover))] text-foreground">
                              <NutriTable f={f} />
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <input type="number" value={f.grammi} onChange={(e) => updateGrams(meal.id, f.id, e.target.value)} className="w-16 rounded bg-black/40 px-2 py-1 text-right font-mono-num text-xs outline-none" data-testid={`food-grams-${f.id}`} />
                        <span className="text-[11px] text-muted-foreground">{f.unit === "pezzo" ? "pz" : "g"}</span>
                        <div className="hidden shrink-0 items-center gap-2.5 font-mono-num text-[11px] text-white/55 sm:flex" data-testid={`food-macros-${f.id}`}>
                          <span><span className="text-white/35">kcal</span> {fmt(f.kcal)}</span>
                          <span><span className="text-white/35">C</span> {fmt(f.carbo, 1)}</span>
                          <span><span className="text-white/35">P</span> {fmt(f.protAnimali + f.protVegetali, 1)}</span>
                          <span><span className="text-white/35">G</span> {fmt(f.grassi, 1)}</span>
                        </div>
                        <button onClick={() => removeFood(meal.id, f.id)} className="text-muted-foreground hover:text-destructive" data-testid={`food-remove-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-4 border-t border-white/5 bg-black/20 text-center">
                {[
                  ["KCAL", fmt(t.kcal), "hsl(var(--c-kcal))"],
                  ["CARBO", fmt(t.carbo, 1) + "g", "hsl(var(--c-carb))"],
                  ["PROT. ANIMALI", fmt(t.protAnimali, 1) + "g", "hsl(var(--c-prot))"],
                  ["GRASSI", fmt(t.grassi, 1) + "g", "hsl(var(--c-fat))"],
                ].map(([l, v, c]) => (
                  <div key={l} className="py-2.5">
                    <div className="field-label">{l}</div>
                    <div className="mt-0.5 font-mono-num text-sm font-bold" style={{ color: c }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input value={newMealName} onChange={(e) => setNewMealName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMeal()} placeholder="Nome nuovo pasto (es. SECONDO SPUNTINO)" className="lab-input max-w-xs" data-testid="new-meal-input" />
        <button onClick={addMeal} className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20" data-testid="add-meal-btn">
          <Plus className="h-4 w-4" /> Aggiungi Pasto
        </button>
      </div>
    </div>
  );
}
